import React, { useContext, useState } from 'react';

import { Tooltip } from 'antd';

import styled from 'styled-components';

import { showPeriodByDay } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';

import { getContact } from '@hooks/API/Contact/UseContactsList';

import ContactAlertPopover from '@components/ContactAlert/ContactAlertPopover';
import CustomerDrawer from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawer';
import CustomerDrawerAccounting from '@components/ContactDetails/CustomerDetails/Drawer/CustomerDrawerAccounting';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';

import { ProjectTitleCompactContainer, ProjectTitleContainer, ProjectTitlePopoverContainer } from './ProjectTitleUI';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .project-title-container {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        margin-bottom: 0.15rem;

        h1 {
            margin-bottom: 0;
        }
    }
`;

const ProjectTitle = ({
    project,
    compact,
    hidePeriod,
    hideAlert,
    linkToProject,
    noProjectText,
    noClientClick,
    pointer,
    popover,
    small,
}) => {
    const [showPreview, setShowPreview] = useState(null);
    const [{ areaContext }] = useStateValue();
    const isAccounting = areaContext.area === 'accounting';
    const { cachedList } = useContext(ListContext);
    const category = project.ProjectCategoryID
        ? cachedList.projectCategories.items.find((category) => category.ID === project.ProjectCategoryID)
        : null;

    const contact = getContact(project.Contact ? project.Contact.ID : project.CustomerID);

    const getProjectCustomerName = () => {
        if (project.Contact) {
            return project.Contact.Name;
        }
        if (project.CustomerName) {
            return project.CustomerName;
        }
        if (project.CustomerID && contact) {
            return contact.N;
        }
        return '';
    };

    const setContactPreview = () => {
        let cID = null;
        if (project.Contact) {
            cID = project.Contact.ID;
        }
        if (project.CustomerID) {
            cID = project.CustomerID;
        }
        if (cID) {
            setShowPreview(cID);
        }
    };

    const handleCloseContactPreview = () => {
        setShowPreview(null);
    };

    const getProjectName = () => {
        if (project.Name) {
            return project.Name;
        }
        if (project.ProjectName) {
            return project.ProjectName;
        }
        return '';
    };

    const getPeriod = () => {
        if (project.FirstDate) {
            return showPeriodByDay(project.FirstDate, project.LastDate);
        }
        return '';
    };

    const ProductText = () => {
        if (!project.Product) {
            return null;
        }
        if (project.Product.length > 60) {
            return (
                <Tooltip title={project.Product}>
                    <span>{project.Product.substring(0, 59)}(...)</span>
                </Tooltip>
            );
        } else {
            return <span>{project.Product}</span>;
        }
    };

    const ProjectContent = () => {
        const pn = getProjectName();
        const pcn = getProjectCustomerName();

        if (pn && pcn && pn.toLowerCase() !== pcn.toLowerCase()) {
            if (project.Product) {
                return (
                    <Wrapper>
                        <h1>
                            <strong>{pn}</strong>
                            <em>/</em>
                            {project.Product}
                        </h1>
                        <div className="project-title-container">
                            {contact && contact.AL && !hideAlert && (
                                <div className="alert-icon" onClick={(e) => e.stopPropagation()}>
                                    <ContactAlertPopover medium contact={contact} />
                                </div>
                            )}
                            {project.pcn && (
                                <p>
                                    client:{' '}
                                    {noClientClick ? (
                                        <strong>{pcn}</strong>
                                    ) : (
                                        <strong style={{ cursor: 'pointer' }} onClick={setContactPreview}>
                                            {pcn}
                                        </strong>
                                    )}
                                    {!hidePeriod && (
                                        <>
                                            , period: <strong>{getPeriod()}</strong>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>
                    </Wrapper>
                );
            } else {
                return (
                    <Wrapper>
                        <div className="project-title-container">
                            {contact && contact.AL && (
                                <div className="alert-icon" onClick={(e) => e.stopPropagation()}>
                                    <ContactAlertPopover medium contact={contact} />
                                </div>
                            )}
                            <h1>
                                {noClientClick ? (
                                    <strong>{pcn}</strong>
                                ) : (
                                    <strong style={{ cursor: 'pointer' }} onClick={setContactPreview}>
                                        {pcn}
                                    </strong>
                                )}
                                <em>/</em>
                                {pn}
                            </h1>
                        </div>

                        {!hidePeriod && <p>{getPeriod()}</p>}
                    </Wrapper>
                );
            }
        }
        return (
            <Wrapper>
                <div className="project-title-container">
                    {contact && contact.AL && !hideAlert && (
                        <div className="alert-icon" onClick={(e) => e.stopPropagation()}>
                            <ContactAlertPopover medium contact={contact} />
                        </div>
                    )}
                    <h1>
                        {noClientClick ? (
                            <strong>{pn}</strong>
                        ) : (
                            <strong style={{ cursor: 'pointer' }} onClick={setContactPreview}>
                                {pn}
                            </strong>
                        )}
                        {project.Product && (
                            <>
                                <em>/</em>
                                <ProductText />
                            </>
                        )}
                    </h1>
                </div>
                {!hidePeriod && <p>{getPeriod()}</p>}
            </Wrapper>
        );
    };

    return (
        <>
            {compact ? (
                <ProjectTitleCompactContainer
                    $pointer={pointer || linkToProject}
                    onClick={linkToProject ? linkToProject : undefined}
                >
                    <ProjectContent />
                </ProjectTitleCompactContainer>
            ) : popover ? (
                <ProjectTitlePopoverContainer>
                    <NwDivider>PROJECT {category && <> / {category.Name}</>}</NwDivider>
                    <ProjectContent />
                </ProjectTitlePopoverContainer>
            ) : (
                <ProjectTitleContainer $small={small} $pointer={pointer} $noProjectText={noProjectText}>
                    {noProjectText ? (
                        category && (
                            <div className="first-line">
                                <code>{category.Name}</code>
                            </div>
                        )
                    ) : (
                        <div className="first-line">
                            <p>PROJECT</p>
                            {category && <code>{category.Name}</code>}
                            {linkToProject && (
                                <NwLinkButton
                                    icon={byPrefixAndName.fass['expand-wide']}
                                    size="small"
                                    onClick={linkToProject}
                                    label="open project"
                                />
                            )}
                        </div>
                    )}
                    <ProjectContent />
                </ProjectTitleContainer>
            )}
            {showPreview &&
                (isAccounting ? (
                    <CustomerDrawerAccounting customerID={showPreview} onClose={handleCloseContactPreview} />
                ) : (
                    <CustomerDrawer customerID={showPreview} onClose={handleCloseContactPreview} />
                ))}
        </>
    );
};

export default ProjectTitle;
